import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, ScrollRestoration } from 'react-router-dom';
import styled from 'styled-components';

import { Flex } from '../components/Flex';
import { SideMenu } from '../components/SideMenu';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import useWindowDimensions from '../hooks/useWindowDimensions';
import { AtiraLinkPlanType } from '../model/plan/types/AtiraLinkPlanType.enum';
import { appSliceSelectors } from '../redux/app/app.selector';
import { appActions } from '../redux/app/app.slice';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { userSliceSelectors } from '../redux/user/user.selector';
import { userActions } from '../redux/user/user.slice';
import { Breakpoints } from '../theme/Breakpoints';
import { Rounded } from '../theme/Rounded';
import { Shadow } from '../theme/Shadow';
import { Spacing } from '../theme/Spacing';
import { Layout as LayoutComponent } from '../utils/local-antd';
import { RestrictedContentBanner } from './RestrictedContentBanner';

const OutletWrapper = styled(Flex)`
  justify-content: space-between;
  width: 100%;
  flex-direction: column;
  overflow-y: scroll;
  background-color: ${({ theme }) => theme.bgElevated};
`;

const StyledSiderLayout = styled(LayoutComponent.Sider)`
  height: 100vh;
  background-color: ${({ theme }) => theme.main} !important;

  overflow: hidden;
  .ant-layout-sider-trigger {
    display: none !important;
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    .ant-layout-sider-trigger {
      display: block !important;
    }
  }
`;

const SiderWrapper = styled(Flex)<{ collapsed: boolean }>`
  .ant-menu {
    height: 100%;
  }

  .ant-layout-sider {
    max-width: ${({ collapsed }) => (!collapsed ? '250px' : '80px')} !important;
    min-width: ${({ collapsed }) => (!collapsed ? '250px' : '80px')} !important;
    width: ${({ collapsed }) => (!collapsed ? '250px' : '80px')} !important;
    overflow: visible;
  }

  .ant-layout-sider-trigger {
    transition:
      bacgroundColor 0s,
      width 0.2s;
    background-color: ${({ theme }) => theme.lightMain} !important;
  }

  @media (max-width: ${Breakpoints.DESKTOP}) {
    .ant-layout-sider {
      max-width: 50px !important;
      min-width: 50px !important;
      justify-content: center;
      align-items: center;
      display: flex;
      padding: 0;
    }
  }

  .ant-layout-sider-trigger {
    width: ${({ collapsed }) => (!collapsed ? '250px' : '80px')} !important;
    position: absolute;
    bottom: ${Spacing.xxl};
    inset-inline-start: ${({ collapsed }) => (collapsed ? 60 : 230)}px;
    width: 2rem !important;
    height: 2rem;
    align-items: center;
    justify-content: center;
    border-radius: ${Rounded.circle};
    box-shadow: ${Shadow.SM};
    transition: all 0.2s;
    background-color: ${({ theme }) => theme.onMain} !important;
    color: ${({ theme }) => theme.main} !important;
    display: none !important;
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    .ant-layout-sider-trigger {
      display: flex !important;
    }
  }
`;

export const Layout: React.FC = () => {
  const dispatch = useAppDispatch();

  const storedValue = useAppSelector(appSliceSelectors.selectSideMenuCollapsed);
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const showRestrictedBanner =
    user?.plan?.freeTrial?.hasUsed === true &&
    user.plan?.freeTrial?.endsAt === null &&
    user?.plan?.freeTrial?.hasDismissedFreeTrialBanner === false &&
    user?.plan?.type === AtiraLinkPlanType.PLAN_0;

  const [collapsed, setCollapsed] = useState(Boolean(storedValue));

  const { width } = useWindowDimensions();
  const { t } = useTranslation();
  useDocumentTitle(t('app.name').toUpperCase());

  const onCollapse = (newCollapsed: boolean) => {
    setCollapsed(newCollapsed);
    dispatch(appActions.setSideMenuCollapsed(newCollapsed));
  };

  useEffect(() => {
    if (width < 992) {
      setCollapsed(true);
    } else {
      setCollapsed(collapsed);
    }
  }, [collapsed, width]);

  useEffect(() => {
    if (user?._id) {
      dispatch(userActions.getLoggedInUser({ userId: user._id }));
    }
  }, [dispatch, user?._id]);

  return (
    <LayoutComponent style={{ width: '100%', height: '100%' }}>
      {showRestrictedBanner ? <RestrictedContentBanner /> : null}

      <SiderWrapper collapsed={collapsed}>
        <StyledSiderLayout
          style={{ minWidth: '250px' }}
          collapsible
          collapsed={collapsed}
          onCollapse={onCollapse}
        >
          <SideMenu collapsed={collapsed} />
        </StyledSiderLayout>
      </SiderWrapper>

      <OutletWrapper id="outlet-wrapper">
        <Outlet />
        <ScrollRestoration />
      </OutletWrapper>
    </LayoutComponent>
  );
};
